import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom, Subject } from 'rxjs';
import { AuthRepository, UserRole } from '@app/core/auth/auth.repository';
import { StudentCourseService } from './student-course.service';
import { CompaniesService } from './companies.service';

export interface MessageStepTarget {
  studentId: number;
  courseId?: number;
  stepId?: number;
  companyId?: number;
}

@Injectable({ providedIn: 'root' })
export class StepNavigationService {
  readonly openConversation$ = new Subject<number>();
  private requestVersion = 0;
  constructor(private courses: StudentCourseService, private companies: CompaniesService,
    private auth: AuthRepository, private router: Router, private snack: MatSnackBar) {}

  async open(target: MessageStepTarget): Promise<boolean> {
    const version = ++this.requestVersion;
    if (!target.stepId || !target.courseId) {
      this.openConversation$.next(target.studentId);
      return true;
    }
    try {
      const response = await firstValueFrom(this.courses.getStudentCourseChecksheet(target.courseId));
      if (version !== this.requestVersion) return false;
      const rows = Array.isArray(response) ? response : (response as any)?.data || [];
      const step = rows.find(row => Number(row.student_course_step_id) === Number(target.stepId));
      const route = step && this.courses.getActionTypes()[Number(step.actiontype_id)]?.url;
      if (!step || !route) throw new Error('Step unavailable');
      if (target.companyId) this.companies.selectCompany(target.companyId);
      const parent = this.auth.getUser()?.role === UserRole.Student
        ? ['/academy', target.courseId] : ['/supervisor', target.studentId, target.courseId];
      return await this.router.navigate([...parent, {
        outlets: { secondary: [route, { scid: target.courseId, step_id: target.stepId }] },
      }]);
    } catch {
      if (version === this.requestVersion) this.snack.open('This step could not be opened. Please try again.', 'Close', { duration: 5000 });
      return false;
    }
  }
}
